/**
 * Converts a disbursement amount into the words shown on the printed
 * Disbursement Note, e.g. 34500000 -> "Thirty Four Million Five Hundred
 * Thousand Rupees Only".
 *
 * Uses the international scale (thousand / million / billion) to match the
 * signed note, not the local crore / lakh scale.
 */

const UNITS = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];

const TENS = [
  '',
  '',
  'Twenty',
  'Thirty',
  'Forty',
  'Fifty',
  'Sixty',
  'Seventy',
  'Eighty',
  'Ninety',
];

/** Scales are applied largest first, so the order here matters. */
const SCALES: Array<{ value: number; name: string }> = [
  { value: 1_000_000_000, name: 'Billion' },
  { value: 1_000_000, name: 'Million' },
  { value: 1_000, name: 'Thousand' },
];

function belowHundred(value: number): string {
  if (value < 20) {
    return UNITS[value];
  }

  const tens = TENS[Math.floor(value / 10)];
  const unit = value % 10;

  return unit ? `${tens} ${UNITS[unit]}` : tens;
}

function belowThousand(value: number): string {
  if (value < 100) {
    return belowHundred(value);
  }

  const hundreds = `${UNITS[Math.floor(value / 100)]} Hundred`;
  const remainder = value % 100;

  return remainder ? `${hundreds} ${belowHundred(remainder)}` : hundreds;
}

function wholeNumberToWords(value: number): string {
  if (value === 0) {
    return 'Zero';
  }

  const parts: string[] = [];
  let remaining = value;

  for (const scale of SCALES) {
    if (remaining >= scale.value) {
      const count = Math.floor(remaining / scale.value);

      parts.push(`${belowThousand(count)} ${scale.name}`);

      remaining = remaining % scale.value;
    }
  }

  if (remaining > 0) {
    parts.push(belowThousand(remaining));
  }

  return parts.join(' ');
}

/**
 * Returns the full "… Rupees Only" string, including paisa when the amount
 * carries a fractional part. Returns an empty string for a missing or
 * negative amount so the caller can render a placeholder instead.
 */
export function amountInWords(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount)) || Number(amount) < 0) {
    return '';
  }

  const rounded = Math.round(Number(amount) * 100) / 100;

  const rupees = Math.floor(rounded);
  const paisa = Math.round((rounded - rupees) * 100);

  const rupeeWords = `${wholeNumberToWords(rupees)} Rupees`;

  if (paisa > 0) {
    return `${rupeeWords} and ${wholeNumberToWords(paisa)} Paisa Only`;
  }

  return `${rupeeWords} Only`;
}
